import React, { useEffect, useRef, useState } from 'react';

const leftPad = (value, length) => {
  while (value.length < length) {
    value = "0" + value;
  }
  return value;
};

export const formatTimer = (durationMs) => {
  durationMs = Math.floor(durationMs);
  const seconds = Math.floor(durationMs / 1000);
  const ms = Math.floor(durationMs / 10) % 100;
  return `${seconds}:${leftPad(`${ms}`, 2)}`;
};

const useFrameTime = () => {
    const [frameTime, setFrameTime] = React.useState(performance.now());
    React.useEffect(() => {
        let frameId;
        const frame = (time) => {
            setFrameTime(time);
            frameId = requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
        return () => cancelAnimationFrame(frameId);
    }, []);
    return frameTime;
};

const PongGame = () => {
    const gameRef = useRef(null);
    const [paddleX, setPaddleX] = useState(window.innerWidth / 2 - 50);
    const [balls, setBalls] = useState([]);
    const [countdown, setCountdown] = useState(10);
    const [paused, setPaused] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameWidth, setGameWidth] = useState(0);
    const [gameHeight, setGameHeight] = useState(0);
    const [paddleHeight, setPaddleHeight] = useState(0);
    const [paddleWidth, setPaddleWidth] = useState(0);
    let ballRadius = (gameWidth + gameHeight) * 0.01;
    const ballTravelTime = 2; // seconds to reach bottom from top

    const [startTime, setStartTime] = React.useState(0);
    const [pauseTime, setPauseTime] = React.useState(0);
    const frameTime = useFrameTime();
    const displayTime = paused ? pauseTime : frameTime - startTime;

    const getRandomBall = () => {
        const angle = Math.random() * Math.PI / 3 + Math.PI / 3; // Random angle between 60 to 120 downward
        const speed = 6;
        const dy = speed * Math.sin(angle);
        const dx = speed * Math.cos(angle);
        const x = gameWidth/4 + Math.random() * (gameWidth/2) + ballRadius;
        const y = 100;
        return { x, y, dx, dy };
    };

    const reset = () => {
        console.log("reset");
        setGameOver(false);
        const rect = gameRef.current.getBoundingClientRect();
        setGameWidth(rect.width);
        setGameHeight(rect.height);
        setBalls([getRandomBall()]);
        setPaddleHeight(gameHeight * 0.05);
        setPaddleWidth(gameWidth * 0.2);
        ballRadius = (gameWidth + gameHeight) * 0.01;
    };

    useEffect(() => {
        if (paused || gameOver) return;
        if (!gameRef.current) return;

        let currentPaddleX = paddleX;

        const handleMouseMove = (e) => {
            if (paused) return;
            const newPaddleX = Math.min(
                Math.max(e.clientX - paddleWidth / 2, 0),
                gameWidth - paddleWidth
            );
            currentPaddleX = newPaddleX;
            gameRef.current.style.setProperty('--paddle-x', `${newPaddleX}px`);
        };

        const handleKeyDown = (e) => {
            if (paused || gameOver) return;
            let moveBy = 20;
            if (e.key === 'ArrowLeft') {
                setPaddleX((prev) => Math.max(prev - moveBy, 0));
            } else if (e.key === 'ArrowRight') {
                setPaddleX((prev) => Math.min(prev + moveBy, gameWidth - paddleWidth));
            }
        };

        let touchStartX = 0;

        const handleTouchStart = (e) => {
            touchStartX = e.touches[0].clientX;
        };

        const handleTouchMove = (e) => {
            if (paused) return;
            const touchX = e.touches[0].clientX;
            let newPaddleX = currentPaddleX + (touchX - touchStartX);
            newPaddleX = Math.min(Math.max(newPaddleX, 0), gameWidth - paddleWidth);
            currentPaddleX = newPaddleX;
            gameRef.current.style.setProperty('--paddle-x', `${newPaddleX}px`);
            touchStartX = touchX;
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', (e) => {
            if (['ArrowLeft', 'ArrowRight'].includes(e.key)) e.preventDefault();
            handleKeyDown(e);
        });
        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [paused, gameOver]);

    useEffect(() => {
        if (paused || gameOver) return;
        if (!gameRef.current) return;

        reset();

        const addBallInterval = setInterval(() => {
            setBalls((prevBalls) => [
                ...prevBalls,
                getRandomBall()
            ]);
            setPaddleWidth(prevWidth => prevWidth + ballRadius);
            setCountdown(10);
        }, 10000);

        const countdownInterval = setInterval(() => {
            setCountdown((prev) => (prev > 0 ? prev - 1 : 10));
        }, 1000);

        const timeInterval = setInterval(() => {
            setElapsedTime((prev) => prev + 1);
        }, 1000);

        const gameLoop = setInterval(() => {
            setBalls((prevBalls) =>
                prevBalls.map((ball) => {
                    let { x, y, dx, dy } = ball;

                    if (x + dx > gameWidth - ballRadius || x + dx < ballRadius) dx = -dx;
                    if (y + dy < ballRadius) dy = Math.abs(dy);

                    const paddleX = parseFloat(getComputedStyle(gameRef.current).getPropertyValue('--paddle-x'));

                    if (
                        y + dy > gameHeight - paddleHeight - ballRadius &&
                        x > paddleX &&
                        x < paddleX + paddleWidth
                    ) dy = -Math.abs(dy);

                    if (y + dy > gameHeight - ballRadius) {
                        clearInterval(gameLoop);
                        clearInterval(addBallInterval);
                        clearInterval(countdownInterval);
                        clearInterval(timeInterval);
                        setGameOver(true);
                    }

                    return { x: x + dx, y: y + dy, dx, dy };
                })
            );
        }, 16);

        return () => {
            clearInterval(addBallInterval);
            clearInterval(countdownInterval);
            clearInterval(gameLoop);
            clearInterval(timeInterval);
        };
    }, [paused, gameOver]);

    const restartGame = () => {
        setBalls([{ x: window.innerWidth / 2, y: 100, dx: 1, dy: 3 }]);
        setGameOver(false);
    };

    const shareScore = () => {
        const shareText = `I scored ${balls.length} in Pong Game! Try to beat my score!`;
        if (navigator.share) {
            navigator.share({
                title: 'Pong Game Score',
                text: shareText,
                url: window.location.href
            });
        } else {
            alert('Sharing not supported on this device.');
        }
    };

    return (
        <div
            ref={gameRef}
            style={{
                position: 'relative',
                width: '100vw',
                height: 'calc(100vh - 100px)',
                background: '#000',
                overflow: 'hidden',
                touchAction: 'none',
                margin: '0 auto',
                border: '2px solid #fff',
                color: '#fff',
                fontFamily: 'Arial',
                '--paddle-x': `${paddleX}px`,
            }}
        >
            {!gameOver ? (
                <>
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 'var(--paddle-x)',
                            width: paddleWidth,
                            height: paddleHeight,
                            background: '#fff',
                        }}
                    />
                    <button
                        onClick={() => setPaused(!paused)}
                        style={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            padding: '5px 10px',
                            background: '#fff',
                            color: '#000',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        {paused ? 'Resume' : 'Pause'}
                    </button>
                    <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)' }}>
                        Balls: {balls.length} | Time: {formatTimer(displayTime)}s
                    </div>
                    <div>Next ball in: {countdown} seconds</div>
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 'var(--paddle-x)',
                            width: paddleWidth,
                            height: paddleHeight,
                            background: '#fff',
                        }}
                    />
                    {balls.map((ball, index) => (
                        <div
                            key={index}
                            style={{
                                position: 'absolute',
                                width: ballRadius * 2,
                                height: ballRadius * 2,
                                borderRadius: '50%',
                                background: 'red',
                                left: ball.x - ballRadius,
                                top: ball.y - ballRadius,
                            }}
                        />
                    ))}
                </>
            ) : (
                <div style={{ color: 'white', textAlign: 'center', marginTop: '40vh' }}>
                    <h2>Game Over!</h2>
                    <p>Your score: {balls.length}</p>
                    <button onClick={shareScore}>Share Score</button>
                    <button onClick={reset}>Restart Game</button>
                </div>
            )}
        </div>
    );
};

export default PongGame;
